import { useEffect, useState } from 'react';
import { Field, Notice, SelectField, TextAreaField } from '../../components/ui.js';
import { LISTS } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import type { LocalRecord } from '../../offline/store.js';

/** The receipt's fields as the form edits them, as text. */
export interface ReceiptDraft {
  vendor: string;
  spent_on: string;
  currency: string;
  total: string;
  tax_gst: string;
  tax_qst: string;
  tax_vat: string;
  budget_line: string;
  event_id: string;
  client: string;
  guests: string;
  purpose: string;
}

export const today = () => new Date().toISOString().slice(0, 10);

const AMOUNTS = ['total', 'tax_gst', 'tax_qst', 'tax_vat'] as const;

/** Text fields to the values the sync API takes (amounts in cents, empty as null). */
export function toFields(d: ReceiptDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(d)) {
    const v = value.trim();
    out[key] = (AMOUNTS as readonly string[]).includes(key)
      ? v === ''
        ? null
        : Math.round(Number(v) * 100) / 100
      : v || null;
  }
  return out;
}

type Errors = Partial<Record<keyof ReceiptDraft, string>>;

/** Required: vendor, date, total, budget line; the taxes never above the total (section 7.8). */
export function checkReceipt(d: ReceiptDraft): Errors {
  const errors: Errors = {};
  if (!d.vendor.trim()) errors.vendor = 'Vendor is required.';
  if (!d.spent_on) errors.spent_on = 'Date is required.';
  else if (d.spent_on > today()) errors.spent_on = 'The date cannot be in the future.';
  const total = Number(d.total);
  if (d.total.trim() === '' || !(total > 0)) errors.total = 'Total is required.';
  if (!d.budget_line) errors.budget_line = 'Budget line is required.';
  for (const key of AMOUNTS) {
    const v = d[key].trim();
    if (v !== '' && (!Number.isFinite(Number(v)) || Number(v) < 0)) {
      errors[key] = 'Not a valid amount.';
    }
  }
  const taxes = ['tax_gst', 'tax_qst', 'tax_vat'].reduce(
    (n, k) => n + (Number(d[k as keyof ReceiptDraft]) || 0),
    0,
  );
  if (!errors.total && taxes > total) errors.total = 'Taxes are higher than the total.';
  return errors;
}

/**
 * The receipt's fields (brief section 7.8), as docs/prototype.html's Check
 * expense: Receipt (vendor, date, currency, total, the three taxes) and
 * Assign (budget line, event, client, guests, business purpose).
 */
export function ReceiptFields({
  value,
  onChange,
  errors,
}: {
  value: ReceiptDraft;
  onChange(value: ReceiptDraft): void;
  errors: Errors;
}) {
  const { store } = useOffline();
  const [events, setEvents] = useState<LocalRecord[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  useEffect(() => {
    let current = true;
    void (async () => {
      if (!store) return;
      const [evs, contacts, leads] = await Promise.all([
        store.listRecords('event'),
        store.listRecords('contact'),
        store.listRecords('lead'),
      ]);
      if (!current) return;
      setEvents(evs.filter((e) => !e.deleted));
      // Client: pick a company the device knows, or type one.
      const names = new Set<string>();
      for (const c of contacts)
        if (typeof c.company === 'string' && c.company) names.add(c.company);
      for (const l of leads) {
        if (typeof l.company_name === 'string' && l.company_name) names.add(l.company_name);
      }
      setClients([...names].sort((a, b) => a.localeCompare(b)));
    })();
    return () => {
      current = false;
    };
  }, [store]);

  const set = (key: keyof ReceiptDraft, v: string) => onChange({ ...value, [key]: v });
  const currencies = [...new Set([...LISTS.expenseCurrencies, value.currency].filter(Boolean))];
  const shownEvents = events.filter((e) => e.status !== 'completed' || e.id === value.event_id);

  return (
    <>
      <div className="formsec">
        <h3>Receipt</h3>
        <Field
          label="Vendor"
          mark
          value={value.vendor}
          error={errors.vendor}
          onChange={(e) => set('vendor', e.target.value)}
        />
        <div className="g2">
          <Field
            label="Date"
            mark
            type="date"
            max={today()}
            value={value.spent_on}
            error={errors.spent_on}
            onChange={(e) => set('spent_on', e.target.value)}
          />
          <SelectField
            label="Currency"
            value={value.currency}
            onChange={(e) => set('currency', e.target.value)}
          >
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </SelectField>
        </div>
        <Field
          label="Total paid, taxes and tip included"
          mark
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          value={value.total}
          error={errors.total}
          onChange={(e) => set('total', e.target.value)}
        />
        <div className="g3">
          <Field
            label="GST / HST"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            value={value.tax_gst}
            error={errors.tax_gst}
            onChange={(e) => set('tax_gst', e.target.value)}
          />
          <Field
            label="QST"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            value={value.tax_qst}
            error={errors.tax_qst}
            onChange={(e) => set('tax_qst', e.target.value)}
          />
          <Field
            label="VAT or other tax"
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            value={value.tax_vat}
            error={errors.tax_vat}
            onChange={(e) => set('tax_vat', e.target.value)}
          />
        </div>
      </div>
      <div className="formsec">
        <h3>Assign</h3>
        <SelectField
          label="Budget line"
          mark
          value={value.budget_line}
          error={errors.budget_line}
          onChange={(e) => set('budget_line', e.target.value)}
        >
          <option value="">Select a budget line</option>
          {LISTS.budgetLines.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </SelectField>
        <div className="g2">
          <SelectField
            label="Event"
            value={value.event_id}
            onChange={(e) => set('event_id', e.target.value)}
          >
            <option value="">No event</option>
            {shownEvents.map((e) => (
              <option key={e.id} value={e.id}>
                {String(e.event_code)}
              </option>
            ))}
          </SelectField>
          <Field
            label="Client"
            hint="Optional, pick or type"
            list="ex-clients"
            autoComplete="off"
            value={value.client}
            onChange={(e) => set('client', e.target.value)}
          />
          <datalist id="ex-clients">
            {clients.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        </div>
        <Field
          label="Guests"
          hint="For meals and entertainment: who was there and their company"
          value={value.guests}
          onChange={(e) => set('guests', e.target.value)}
        />
        <TextAreaField
          label="Business purpose"
          rows={2}
          placeholder="Dinner with prospect after booth demo"
          value={value.purpose}
          onChange={(e) => set('purpose', e.target.value)}
        />
      </div>
      {Object.keys(errors).length > 0 && (
        <Notice kind="error">Fill in the fields highlighted in red.</Notice>
      )}
    </>
  );
}
